import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
    Card,
    CardContent,
    Typography,
    makeStyles,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    Snackbar,
} from '@material-ui/core'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import { useIntl } from 'react-intl'

import { CapeeshColors } from '../../../assets/ColorPalette'
import { i18nMessages } from '../../../Logic/CodeAccessible.i18n.Messages'
import { userService } from '../../../_state/services'

const useStyles = makeStyles(() => ({
    root: {
        marginLeft: '25px',
        marginRight: '25px',
        padding: '20px',
    },
    pageTitle: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '24px',
        color: CapeeshColors.DarkText,
    },
    pageDescription: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '14px',
        color: CapeeshColors.Gray1,
        marginTop: '4px',
        marginBottom: '20px',
        maxWidth: '820px',
    },
    grantRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '8px',
    },
    grantHint: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        marginBottom: '20px',
    },
    tableHeaderCell: {
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Rubik',
        textAlign: 'left',
    },
    tableCell: {
        fontSize: '14px',
        fontWeight: 400,
        fontFamily: 'Rubik',
    },
    mutedCell: {
        fontSize: '13px',
        fontWeight: 400,
        fontFamily: 'Rubik',
        color: CapeeshColors.Gray1,
    },
    chip: {
        marginRight: '6px',
        marginBottom: '4px',
    },
    dialogText: {
        fontFamily: 'Rubik',
        fontSize: '14px',
    },
}))

const OverallAccess = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { user } = props

    const [overview, setOverview] = useState(null)
    const [loading, setLoading] = useState(true)
    const [email, setEmail] = useState('')
    const [granting, setGranting] = useState(false)
    const [userToRevoke, setUserToRevoke] = useState(null)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    const isSuperAdmin = user?.isSuperAdmin === true

    const showError = (error, fallbackMessage) => {
        const serverMessage = typeof error?.response?.data === 'string' ? error.response.data : null
        setSnackbar({ open: true, message: serverMessage || fallbackMessage, severity: 'error' })
    }

    const loadOverview = async () => {
        setLoading(true)
        try {
            const response = await userService.getCapeeshAdmins()
            setOverview(response.data)
        } catch (error) {
            console.error('Failed to load Capeesh admins', error)
            showError(error, intl.formatMessage(i18nMessages.capeeshAccessLoadFailed))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!isSuperAdmin) {
            return
        }
        loadOverview()
    }, [isSuperAdmin])

    const handleGrant = async () => {
        if (!email.trim()) {
            return
        }

        setGranting(true)
        try {
            await userService.grantAllOrganizationsReporting({ email: email.trim() })
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.capeeshAccessGranted, { email: email.trim() }),
                severity: 'success',
            })
            setEmail('')
            loadOverview()
        } catch (error) {
            console.error('Failed to grant reporting access', error)
            showError(error, intl.formatMessage(i18nMessages.capeeshAccessLoadFailed))
        } finally {
            setGranting(false)
        }
    }

    const handleRevoke = async () => {
        const target = userToRevoke
        setUserToRevoke(null)
        if (!target) {
            return
        }

        try {
            await userService.revokeAllOrganizationsReporting({ userId: target.userId })
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.capeeshAccessRevoked),
                severity: 'success',
            })
            loadOverview()
        } catch (error) {
            console.error('Failed to revoke reporting access', error)
            showError(error, intl.formatMessage(i18nMessages.capeeshAccessLoadFailed))
        }
    }

    if (!isSuperAdmin) {
        return <div className={classes.root} />
    }

    return (
        <div className={classes.root}>
            <Typography className={classes.pageTitle}>{intl.formatMessage(i18nMessages.capeeshAccessTitle)}</Typography>
            <Typography className={classes.pageDescription}>
                {intl.formatMessage(i18nMessages.capeeshAccessDescription)}
            </Typography>

            <Card>
                <CardContent>
                    <div className={classes.grantRow}>
                        <TextField
                            label={intl.formatMessage(i18nMessages.capeeshAccessGrantLabel)}
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            variant="outlined"
                            size="small"
                            style={{ minWidth: '320px' }}
                            disabled={granting}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={granting || !email.trim()}
                            onClick={handleGrant}>
                            {intl.formatMessage(i18nMessages.capeeshAccessGrantButton)}
                        </Button>
                        {granting && <CircularProgress size={22} />}
                    </div>
                    <Typography className={classes.grantHint}>
                        {intl.formatMessage(i18nMessages.capeeshAccessGrantHint, {
                            domains: (overview?.allowedEmailDomains || []).join(', '),
                        })}
                    </Typography>

                    {loading ? (
                        <CircularProgress size={26} />
                    ) : (overview?.users?.length ?? 0) === 0 ? (
                        <Typography className={classes.tableCell}>
                            {intl.formatMessage(i18nMessages.capeeshAccessNoUsers)}
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHeaderCell}>
                                            {intl.formatMessage(i18nMessages.capeeshAccessColumnName)}
                                        </TableCell>
                                        <TableCell className={classes.tableHeaderCell}>
                                            {intl.formatMessage(i18nMessages.capeeshAccessColumnEmail)}
                                        </TableCell>
                                        <TableCell className={classes.tableHeaderCell}>
                                            {intl.formatMessage(i18nMessages.capeeshAccessColumnAccess)}
                                        </TableCell>
                                        <TableCell className={classes.tableHeaderCell}>
                                            {intl.formatMessage(i18nMessages.capeeshAccessColumnOrganizations)}
                                        </TableCell>
                                        <TableCell className={classes.tableHeaderCell} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {overview.users.map((accessUser) => (
                                        <TableRow key={accessUser.userId}>
                                            <TableCell className={classes.tableCell}>{accessUser.name}</TableCell>
                                            <TableCell className={classes.tableCell}>{accessUser.email}</TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {accessUser.isSuperAdmin && (
                                                    <Chip
                                                        className={classes.chip}
                                                        size="small"
                                                        label={intl.formatMessage(i18nMessages.capeeshAccessSuperAdmin)}
                                                    />
                                                )}
                                                {accessUser.hasAllOrganizationsReporting && (
                                                    <Chip
                                                        className={classes.chip}
                                                        size="small"
                                                        color="primary"
                                                        label={intl.formatMessage(
                                                            i18nMessages.capeeshAccessReportingEverywhere
                                                        )}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell className={classes.mutedCell}>
                                                {accessUser.organizationRoles?.length > 0
                                                    ? accessUser.organizationRoles
                                                          .map((x) => `${x.organizationName} (${x.role})`)
                                                          .join(', ')
                                                    : intl.formatMessage(i18nMessages.capeeshAccessNoOrganizations)}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {accessUser.hasAllOrganizationsReporting ? (
                                                    <Button size="small" onClick={() => setUserToRevoke(accessUser)}>
                                                        {intl.formatMessage(i18nMessages.capeeshAccessRevoke)}
                                                    </Button>
                                                ) : (
                                                    <Typography className={classes.mutedCell}>
                                                        {intl.formatMessage(i18nMessages.capeeshAccessSuperAdminNote)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={userToRevoke !== null} onClose={() => setUserToRevoke(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{intl.formatMessage(i18nMessages.capeeshAccessRevokeConfirmTitle)}</DialogTitle>
                <DialogContent>
                    <Typography className={classes.dialogText}>
                        {intl.formatMessage(i18nMessages.capeeshAccessRevokeConfirmBody, {
                            name: userToRevoke?.name || userToRevoke?.email || '',
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserToRevoke(null)}>
                        {intl.formatMessage(i18nMessages.capeeshAccessCancel)}
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleRevoke}>
                        {intl.formatMessage(i18nMessages.capeeshAccessConfirm)}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

const mapStateToProps = (state) => {
    const { user } = state
    return {
        user,
    }
}

export default connect(mapStateToProps)(OverallAccess)
